import { Component, OnInit, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import {
  MONTH_PAD_THRESHOLD,
  CHART_CONFIG,
  CHART_MESSAGES
} from '@app/shared/constants/constants';
import { PageTitle, Priority, ChartDefaults } from '@app/shared/constants/global.enum';
import { VendorService } from '@app/shared/services/vendor.service';
import { DataService } from '@app/shared/services/data.service';
import { ChartUtilities, ChartDataItem, SegmentAngle } from '@app/shared/utils/chartData';
import { Subscription } from 'rxjs';
import { iconSizeInRem } from '@shared/config/allowedKeys.config';

@Component({
  selector: 'app-doughnut',
  templateUrl: './doughnut.component.html',
  styleUrls: ['./doughnut.component.scss'],
})
export class DoughnutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('doughnutCanvas') doughnutCanvas!: ElementRef<HTMLCanvasElement>;
  chartData: ChartDataItem[] = [];
  private canvasContext?: CanvasRenderingContext2D;
  private animationProgress: number = 0;
  private isAnimating: boolean = false;
  private hoveredSegment: number = -1;
  private segmentAngles: SegmentAngle[] = [];

  errorMessage: string | null = null;
  vendorDetails: any[] = [];
  vendorLegendItems: any[] = [];
  monthDropdownOptions: { value: string; label: string }[] = [];
  selectedOption: string = '';
  month: number = 0;
  year: number = 0;
  priority = Priority;
  pageTitle = PageTitle.TopVendors;
  isDataLoaded: boolean = false;
  isLoading: boolean = false;
  loadingText = CHART_MESSAGES.LOADING_VENDOR_DATA;
  private dataSub?: Subscription;
  imageSrc: string = 'assets/img/no-data-found.png';
  employeeId: string = '';
  fontSize_opentab = iconSizeInRem;

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    private vendorService: VendorService,
    private dataService: DataService,
  ) {}

  ngOnInit(): void {
    const userProfile = this.dataService.userProfile();
    this.employeeId = userProfile?.employeeId || '';
    this.generateMonthDropdownOptions();
    this.selectedOption = this.monthDropdownOptions[ChartDefaults.DROPDOWN_FIRST_INDEX]?.value;
    this.setMonthAndYear(this.selectedOption);
    this.isLoading = true;
    this.loadVendorDetails(this.month, this.year);
  }

  ngAfterViewInit(): void {
    if (this.isDataLoaded && this.chartData.length > 0) {
      this.renderChart();
    }
  }

  private renderChart(): void {
    if (!this.doughnutCanvas || !this.chartData || this.chartData.length === 0) {
      return;
    }

    const canvas = this.doughnutCanvas.nativeElement;
    const container = canvas.parentElement;
    const finalSize = Math.max(CHART_CONFIG.CHART.MIN_CANVAS_SIZE, ChartUtilities.calculateCanvasSize(container));

    const context = ChartUtilities.setupHighDPICanvas(canvas, finalSize);
    if (!context) return;

    this.canvasContext = context;
    canvas.onmousemove = (event) => this.onCanvasMouseMove(event);
    canvas.onmouseleave = () => this.onCanvasMouseLeave();

    this.canvasContext.clearRect(0, 0, finalSize, finalSize);
    this.startAnimation(finalSize);
  }

  private startAnimation(canvasSize: number): void {
    if (this.isAnimating) return;

    this.isAnimating = true;
    this.animationProgress = 0;

    const animate = () => {
      this.animationProgress += CHART_CONFIG.ANIMATION.SPEED;

      if (this.animationProgress >= 1) {
        this.animationProgress = 1;
        this.isAnimating = false;
      }

      if (this.canvasContext) {
        this.canvasContext.clearRect(0, 0, canvasSize, canvasSize);
        this.drawDoughnutChart(this.canvasContext, canvasSize, this.animationProgress);
      }

      if (this.isAnimating) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  onCanvasMouseMove(event: MouseEvent): void {
    if (!this.doughnutCanvas || !this.canvasContext) return;

    const canvas = this.doughnutCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (event.clientX - rect.left) * scaleX / (window.devicePixelRatio || 1);
    const y = (event.clientY - rect.top) * scaleY / (window.devicePixelRatio || 1);

    const centerX = (canvas.width / (window.devicePixelRatio || 1)) / 2;
    const centerY = (canvas.height / (window.devicePixelRatio || 1)) / 2;

    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx) + Math.PI / 2;
    const normalizedAngle = ChartUtilities.normalizeAngle(angle);

    const outerRadius = Math.min(centerX, centerY) - CHART_CONFIG.CHART.OUTER_RADIUS_OFFSET;
    const innerRadius = outerRadius * CHART_CONFIG.CHART.INNER_RADIUS_RATIO;

    if (distance >= innerRadius && distance <= outerRadius) {
      let hoveredIndex = -1;
      for (let i = 0; i < this.segmentAngles.length; i++) {
        const segment = this.segmentAngles[i];
        if (normalizedAngle >= segment.start && normalizedAngle <= segment.end) {
          hoveredIndex = i;
          break;
        }
      }

      if (hoveredIndex !== this.hoveredSegment) {
        this.hoveredSegment = hoveredIndex;
        const canvasSize = Math.min(canvas.width, canvas.height) / (window.devicePixelRatio || 1);
        this.canvasContext.clearRect(0, 0, canvas.width / (window.devicePixelRatio || 1), canvas.height / (window.devicePixelRatio || 1));
        this.drawDoughnutChart(this.canvasContext, canvasSize, 1);
      }
    } else if (this.hoveredSegment !== -1) {
      this.hoveredSegment = -1;
      const canvasSize = Math.min(canvas.width, canvas.height) / (window.devicePixelRatio || 1);
      this.canvasContext.clearRect(0, 0, canvas.width / (window.devicePixelRatio || 1), canvas.height / (window.devicePixelRatio || 1));
      this.drawDoughnutChart(this.canvasContext, canvasSize, 1);
    }
  }

  onCanvasMouseLeave(): void {
    if (this.hoveredSegment !== -1) {
      this.hoveredSegment = -1;
      if (this.canvasContext && this.doughnutCanvas) {
        const canvas = this.doughnutCanvas.nativeElement;
        const canvasSize = Math.min(canvas.width, canvas.height) / (window.devicePixelRatio || 1);
        this.canvasContext.clearRect(0, 0, canvas.width / (window.devicePixelRatio || 1), canvas.height / (window.devicePixelRatio || 1));
        this.drawDoughnutChart(this.canvasContext, canvasSize, 1);
      }
    }
  }


  private drawDoughnutChart(ctx: CanvasRenderingContext2D, size: number, animationProgress: number = 1): void {
    const centerX = size / 2;
    const centerY = size / 2;
    const outerRadius = Math.min(centerX, centerY) - CHART_CONFIG.CHART.CHART_OFFSET;
    const innerRadius = outerRadius * CHART_CONFIG.CHART.INNER_RADIUS_RATIO;
    const total = this.chartData.reduce((sum, item) => sum + item.count, 0);

    if (total === 0) {
      this.drawNoDataMessage(ctx, centerX, centerY);
      return;
    }

    this.segmentAngles = [];

    let currentAngle = -Math.PI / 2;
    this.chartData.forEach((item, index) => {
      const fullSliceAngle = (item.count / total) * 2 * Math.PI;
      const sliceAngle = fullSliceAngle * animationProgress;

      if (animationProgress >= 1) {
        this.segmentAngles.push({
          start: ChartUtilities.normalizeAngle(currentAngle + Math.PI / 2),
          end: ChartUtilities.normalizeAngle(currentAngle + fullSliceAngle + Math.PI / 2),
          index: index
        });
      }

      ctx.beginPath();
      ctx.arc(centerX, centerY, outerRadius, currentAngle, currentAngle + sliceAngle);
      ctx.arc(centerX, centerY, innerRadius, currentAngle + sliceAngle, currentAngle, true);
      ctx.closePath();

      if (this.hoveredSegment === index) {
        ctx.fillStyle = this.adjustBrightness(item.color, CHART_CONFIG.CHART.BRIGHTNESS_FACTOR);
      } else {
        ctx.fillStyle = item.color;
      }
      ctx.fill();

      ctx.strokeStyle = CHART_CONFIG.COLORS.WHITE;
      ctx.lineWidth = CHART_CONFIG.STROKE.CHART_WIDTH;
      ctx.stroke();

      currentAngle += fullSliceAngle;
    });

    if (animationProgress >= 1) {
      this.drawOptimizedLabels(ctx, centerX, centerY, outerRadius, innerRadius, size);
    }
  }

  private drawOptimizedLabels(ctx: CanvasRenderingContext2D, centerX: number, centerY: number, outerRadius: number, innerRadius: number, size: number): void {
    const total = this.chartData.reduce((sum, item) => sum + item.count, 0);
    let currentAngle = -Math.PI / 2;

    this.chartData.forEach((item, index) => {
      const fullSliceAngle = (item.count / total) * 2 * Math.PI;
      const midAngle = currentAngle + fullSliceAngle / 2;

      const labelRadius = outerRadius + CHART_CONFIG.CHART.LABEL_RADIUS_OFFSET;
      const labelX = centerX + Math.cos(midAngle) * labelRadius;
      const labelY = centerY + Math.sin(midAngle) * labelRadius;

      ctx.fillStyle = CHART_CONFIG.COLORS.BLACK;
      ctx.font = `${CHART_CONFIG.FONTS.LABEL_COUNT_WEIGHT} ${CHART_CONFIG.FONTS.LABEL_COUNT_SIZE} ${CHART_CONFIG.FONTS.FAMILY}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(item.count.toString(), labelX, labelY);

      if (this.hoveredSegment === index) {
        this.drawSimpleTooltip(ctx, item, midAngle, centerX, centerY, outerRadius, size);
      }

      currentAngle += fullSliceAngle;
    });
  }

  private drawSimpleTooltip(ctx: CanvasRenderingContext2D, item: any, angle: number, centerX: number, centerY: number, outerRadius: number, size: number): void {
    const vendorName = item.vendorName.toUpperCase();
    const countText = item.count.toString();

    ctx.font = `${CHART_CONFIG.FONTS.TOOLTIP_NAME_WEIGHT} ${CHART_CONFIG.FONTS.TOOLTIP_NAME_SIZE} ${CHART_CONFIG.FONTS.FAMILY}`;
    const nameWidth = ctx.measureText(vendorName).width;
    ctx.font = `${CHART_CONFIG.FONTS.TOOLTIP_COUNT_WEIGHT} ${CHART_CONFIG.FONTS.TOOLTIP_COUNT_SIZE} ${CHART_CONFIG.FONTS.FAMILY}`;
    const countWidth = ctx.measureText(countText).width;

    const padding = CHART_CONFIG.TOOLTIP.PADDING;
    const colorSquareSize = CHART_CONFIG.TOOLTIP.COLOR_SQUARE_SIZE;
    const tooltipWidth = Math.max(nameWidth, colorSquareSize + countWidth + CHART_CONFIG.TOOLTIP.COLOR_COUNT_SPACING) + (padding * 2);
    const tooltipHeight = CHART_CONFIG.TOOLTIP.HEIGHT;

    const tooltipDistance = outerRadius + CHART_CONFIG.TOOLTIP.DISTANCE_OFFSET;
    let tooltipX = centerX + Math.cos(angle) * tooltipDistance;
    let tooltipY = centerY + Math.sin(angle) * tooltipDistance;

    const margin = CHART_CONFIG.TOOLTIP.MARGIN;
    tooltipX = Math.max(margin, Math.min(tooltipX - tooltipWidth/2, size - tooltipWidth - margin));
    tooltipY = Math.max(margin, Math.min(tooltipY - tooltipHeight/2, size - tooltipHeight - margin));

    ctx.fillStyle = CHART_CONFIG.COLORS.TOOLTIP_BACKGROUND;
    ctx.fillRect(tooltipX, tooltipY, tooltipWidth, tooltipHeight);

    ctx.fillStyle = CHART_CONFIG.COLORS.WHITE;
    ctx.font = `${CHART_CONFIG.FONTS.TOOLTIP_NAME_WEIGHT} ${CHART_CONFIG.FONTS.TOOLTIP_NAME_SIZE} ${CHART_CONFIG.FONTS.FAMILY}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(vendorName, tooltipX + tooltipWidth/2, tooltipY + 15);

    const contentY = tooltipY + 30;
    const startX = tooltipX + (tooltipWidth - colorSquareSize - countWidth - CHART_CONFIG.TOOLTIP.BORDER_SPACING) / 2;

    ctx.fillStyle = item.color;
    ctx.fillRect(startX, contentY - colorSquareSize/2, colorSquareSize, colorSquareSize);

    ctx.strokeStyle = CHART_CONFIG.COLORS.WHITE;
    ctx.lineWidth = CHART_CONFIG.TOOLTIP.BORDER_WIDTH;
    ctx.strokeRect(startX, contentY - colorSquareSize/2, colorSquareSize, colorSquareSize);

    ctx.fillStyle = CHART_CONFIG.COLORS.WHITE;
    ctx.font = `${CHART_CONFIG.FONTS.TOOLTIP_COUNT_WEIGHT} ${CHART_CONFIG.FONTS.TOOLTIP_COUNT_SIZE} ${CHART_CONFIG.FONTS.FAMILY}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(countText, startX + colorSquareSize + CHART_CONFIG.TOOLTIP.BORDER_SPACING, contentY);
  }

  private drawNoDataMessage(ctx: CanvasRenderingContext2D, centerX: number, centerY: number): void {
    ctx.fillStyle = CHART_CONFIG.COLORS.NO_DATA_TEXT;
    ctx.font = `${CHART_CONFIG.FONTS.NO_DATA_WEIGHT} ${CHART_CONFIG.FONTS.NO_DATA_SIZE} ${CHART_CONFIG.FONTS.FAMILY}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(CHART_MESSAGES.NO_DATA_AVAILABLE, centerX, centerY);
  }

  private adjustBrightness(color: string, factor: number): string {
    const hex = color.replace('#', '');
    const r = Math.min(255, Math.floor(parseInt(hex.substr(0, 2), 16) * factor));
    const g = Math.min(255, Math.floor(parseInt(hex.substr(2, 2), 16) * factor));
    const b = Math.min(255, Math.floor(parseInt(hex.substr(4, 2), 16) * factor));
    return `rgb(${r}, ${g}, ${b})`;
  }

  generateMonthDropdownOptions(): void {
    const currentDate = new Date();
    const options = [] as { value: string; label: string; selected: boolean }[];
    for (let i = 0; i < ChartDefaults.MONTHS_TO_SHOW; i++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - i,
        1,
      );
      const monthName = date.toLocaleString('default', { month: 'long' });
      const year = date.getFullYear();
      options.push({
        value: `${monthName} ${year}`,
        label: `${monthName}, ${year}`,
        selected: i === ChartDefaults.DROPDOWN_FIRST_INDEX,
      });
    }
    this.monthDropdownOptions = options;
  }

  private setMonthAndYear(selectedOption: string): void {
    const [monthValue, yearValue] = selectedOption.split(' ');
    const monthStr = new Date(`${monthValue} 1, ${yearValue}`).getMonth() + 1;
    const formattedMonth = monthStr < MONTH_PAD_THRESHOLD ? `0${monthStr}` : `${monthStr}`;
    this.month = parseInt(formattedMonth, 10);
    this.year = parseInt(yearValue, 10);
  }

  private loadVendorDetails(month: number, year: number): void {
    if (!this.isLoading) {
      this.isLoading = true;
    }
    this.isDataLoaded = false;
    this.errorMessage = null;
    this.chartData = [];

    this.dataSub = this.vendorService
      .getTopVendors$(month, year, this.employeeId)
      .subscribe({
        next: (vendors) => {
          this.isLoading = false;
          this.isDataLoaded = true;
          this.vendorDetails = Array.isArray(vendors) ? vendors : [];

          if (this.vendorDetails.length === 0) {
            this.errorMessage = CHART_MESSAGES.NO_VENDOR_DATA;
          } else {
            this.initializeChartData();
            setTimeout(() => this.renderChart(), CHART_CONFIG.ANIMATION.DELAY);
          }
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.isLoading = false;
          this.isDataLoaded = true;
          this.errorMessage = error?.message || CHART_MESSAGES.NO_VENDOR_DATA;
          this.vendorDetails = [];
          this.chartData = [];
          this.cdr.detectChanges();
        }
      });
  }

  get rankedVendors(): { name: string; count: number; color: string; widthPct: number }[] {
    if (!this.chartData?.length) return [];
    const max = this.chartData.reduce((m, it) => Math.max(m, it.count || 0), 0);
    return this.chartData
      .map((it: any) => ({
        name: it.name || it.vendorName || 'Vendor',
        count: it.count || 0,
        color: it.color,
        widthPct: max > 0 ? Math.max(4, Math.round((it.count / max) * 100)) : 0,
      }))
      .sort((a, b) => b.count - a.count);
  }

  get totalVendorCount(): number {
    return this.chartData?.reduce((sum, it) => sum + (it.count || 0), 0) ?? 0;
  }

  private initializeChartData(): void {
    this.chartData = ChartUtilities.initializeChartData(this.vendorDetails);
    this.createVendorLegend();
  }
  private createVendorLegend(): void {
    this.vendorLegendItems = this.chartData?.map((item: any, index: number) => ({
      id: `vendor-${index}`,
      label: item.name || item.vendorName || `Vendor ${index + 1}`,
      colorClass: `vendor-color-${index}`,
      color: item.color
    }));
  }

  onMonthChange(event: any): void {
    const value = this.resolveMonthOptionValue(event);
    if (!value) {
      return;
    }
    this.selectedOption = value;
    this.setMonthAndYear(this.selectedOption);
    this.loadVendorDetails(this.month, this.year);
  }

  /**
   * emerald-dropdown's (onChange) emits the option LABEL ("September, 2026"),
   * while ngModel / setMonthAndYear work with the VALUE ("September 2026").
   * Accept either form and return the matching option value.
   */
  private resolveMonthOptionValue(event: any): string {
    if (event == null) {
      return '';
    }
    if (typeof event === 'object' && 'value' in event) {
      return String(event.value ?? '');
    }
    const raw = String(event);
    const byValue = this.monthDropdownOptions.find((o) => o.value === raw);
    if (byValue) {
      return byValue.value;
    }
    const byLabel = this.monthDropdownOptions.find((o) => o.label === raw);
    return byLabel ? byLabel.value : '';
  }

  retryLoading(): void {
    this.loadVendorDetails(this.month, this.year);
  }

  navigateToVendorsList() {
    this.router.navigate(['vendor-management/vendors-list'],{
      queryParams: {  month: this.month, year: this.year},
    })

  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe();
  }
}
